import { useQuery, type QueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { useCallingEnabled } from '@/hooks/useCalling'
import {
  getAllowedActions,
  getAllowedActionsBatch,
  getMyPermissions,
  getPermissionCatalog,
} from '@/services/api/permission.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore, type PermissionKeyValue } from '@/stores/permission.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { PermissionCatalog } from '@/types/api/permission'

/**
 * Permission-aware UI.
 *
 * The backend is the source of truth: `/permissions/me` says what the user may do workspace-wide,
 * and `/permissions/me/resources/...` what they may do with one resource (record-group scopes,
 * per-resource grants). The UI only hides/disables; the API still enforces every action.
 *
 *  - `can(key)`          — allowed everywhere in the workspace (global grant, admin, owner)
 *  - `canAnywhere(key)`  — allowed on at least one resource (show entry points / navigation)
 *  - `useAllowedActions(type, id).can(key)` — allowed on that resource
 */

export const permissionQueryKeys = {
  all: (workspaceId: string | null) => ['workspace', workspaceId, 'permissions'] as const,
  me: (workspaceId: string | null) => ['workspace', workspaceId, 'permissions', 'me'] as const,
  allowed: (workspaceId: string | null, resourceType: string, resourceId: string) =>
    ['workspace', workspaceId, 'permissions', 'allowed', resourceType, resourceId] as const,
  allowedBatch: (workspaceId: string | null, resourceType: string, resourceIds: string[]) =>
    ['workspace', workspaceId, 'permissions', 'allowed-batch', resourceType, resourceIds] as const,
  effective: (workspaceId: string | null, userId: string) =>
    ['workspace', workspaceId, 'permissions', 'effective', userId] as const,
  catalog: ['permissions', 'catalog'] as const,
}

/** Call after any change to grants, roles or role membership. */
export function invalidatePermissionQueries(queryClient: QueryClient) {
  const workspaceId = useWorkspaceStore.getState().currentWorkspaceId
  return queryClient.invalidateQueries({ queryKey: permissionQueryKeys.all(workspaceId) })
}

export function useMyPermissions() {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: permissionQueryKeys.me(workspaceId),
    queryFn: getMyPermissions,
    // The token identifies the workspace; the id only keys the cache per workspace.
    enabled: !!accessToken,
    staleTime: 30 * 1000,
  })
}

export function usePermission() {
  // isPending (not isLoading) so guards wait while the query is still disabled (no workspace yet).
  const { data, isPending: isLoading } = useMyPermissions()
  // Flags from the login response, used until /permissions/me has loaded.
  const loginFlags = usePermissionStore()

  const globalKeys = useMemo(() => new Set(data?.globalKeys ?? []), [data])
  const anywhereKeys = useMemo(() => new Set(data?.anywhereKeys ?? []), [data])

  const can = useCallback((key: PermissionKeyValue | string) => globalKeys.has(key), [globalKeys])
  const canAnywhere = useCallback((key: PermissionKeyValue | string) => anywhereKeys.has(key), [anywhereKeys])

  const isOwner = data?.owner ?? loginFlags.isSuperUser
  const isDeveloper = data?.developer ?? loginFlags.isDeveloper

  return {
    can,
    canAnywhere,
    /** Some record group is reachable (incl. trace-only access to reach shared content). */
    hasRecordGroupAccess: data?.recordGroupAccess ?? false,
    /** @deprecated use `can` (workspace-wide) or `useAllowedActions` (per resource) */
    hasPermission: can,
    isOwner,
    isAdmin: data?.admin ?? false,
    isDeveloper,
    /** @deprecated use `isOwner` */
    isSuperUser: isOwner,
    isLoading,
  }
}

/** What the current user may do with one resource. */
export function useAllowedActions(resourceType: string, resourceId: string | null | undefined) {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const { data, isLoading, isError } = useQuery({
    queryKey: permissionQueryKeys.allowed(workspaceId, resourceType, resourceId ?? ''),
    queryFn: () => getAllowedActions(resourceType, resourceId!),
    enabled: !!resourceId,
    staleTime: 30 * 1000,
  })
  const allowed = useMemo(() => new Set(data ?? []), [data])
  const can = useCallback((key: PermissionKeyValue | string) => allowed.has(key), [allowed])
  return { can, isLoading, isError }
}

/** Per-resource permissions for a list of resources (one request). */
export function useAllowedActionsBatch(resourceType: string, resourceIds: string[]) {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const ids = useMemo(() => [...resourceIds].sort(), [resourceIds])
  const { data, isLoading } = useQuery({
    queryKey: permissionQueryKeys.allowedBatch(workspaceId, resourceType, ids),
    queryFn: () => getAllowedActionsBatch(resourceType, ids),
    enabled: ids.length > 0,
    staleTime: 30 * 1000,
  })
  const can = useCallback(
    (resourceId: string, key: PermissionKeyValue | string) => (data?.[resourceId] ?? []).includes(key),
    [data]
  )
  return { can, isLoading }
}

/** Calling's permissions are left out while calling is unavailable (their grants stay stored). */
export function usePermissionCatalog() {
  const { enabled: callingEnabled } = useCallingEnabled()
  const select = useCallback(
    (catalog: PermissionCatalog): PermissionCatalog =>
      callingEnabled
        ? catalog
        : {
            ...catalog,
            categories: catalog.categories.filter((c) => c.id !== 'CALLING'),
            resources: catalog.resources.filter((r) => r.category !== 'CALLING'),
          },
    [callingEnabled]
  )
  return useQuery({
    queryKey: permissionQueryKeys.catalog,
    queryFn: getPermissionCatalog,
    staleTime: Infinity,
    select,
  })
}

interface CanProps {
  /** Permission key value, e.g. `PERMISSION_KEYS.ROLE_CREATE`. */
  permission: PermissionKeyValue | string
  /** Match if held on any resource rather than workspace-wide. */
  anywhere?: boolean
  children: ReactNode
  fallback?: ReactNode
}

/** Renders children only if the user holds the permission. */
export function Can({ permission, anywhere = false, children, fallback = null }: CanProps) {
  const { can, canAnywhere } = usePermission()
  const allowed = anywhere ? canAnywhere(permission) : can(permission)
  return <>{allowed ? children : fallback}</>
}

/** @deprecated use {@link Can} */
export const PermissionGate = Can

interface RequirePermissionProps {
  /** Required unless `anyOf` is given. */
  permission?: PermissionKeyValue | string
  /** Allowed if the user holds any of these on at least one resource (alternative to `permission`). */
  anyOf?: (PermissionKeyValue | string)[]
  anywhere?: boolean
  children: ReactNode
  redirectTo?: string
}

/** Route guard: redirects away when the user lacks the permission (after permissions load). */
export function RequirePermission({ permission, anyOf, anywhere = false, children, redirectTo = '/dashboard' }: RequirePermissionProps) {
  const { can, canAnywhere, isLoading } = usePermission()
  if (isLoading) return null
  const allowed = anyOf
    ? anyOf.some((key) => canAnywhere(key))
    : permission !== undefined && (anywhere ? canAnywhere(permission) : can(permission))
  return allowed ? <>{children}</> : <Navigate to={redirectTo} replace />
}
